import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, cn, IconButton, Tooltip } from '@c2fo/liquidity';
import colors from '@c2fo/liquidity/colors';
import { ArrowUpRightFromSquareIcon, CalendarLinesIcon, TrashCanIcon } from '@c2fo/liquidity/icons';
import { TakerMarketWithAutomation } from '@/enterprise/data/useAutomations';
import Divider from '@/shared/components/Divider';
import { IconButtonLink } from '@/shared/components/IconButtonLink';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useRestrictions from '@/utils/useRestrictions';
import { buildCustomScheduleSentenceFromExistingOfferSchedule } from '../../offerModal/utils';
import DeleteScheduleModal from './DeleteScheduleModal';

const MiniScheduleManager = ({
  asChild = false,
  takerMarketWithAutomation,
}: {
  asChild?: boolean;
  takerMarketWithAutomation: TakerMarketWithAutomation;
}) => {
  const { automation } = takerMarketWithAutomation;
  const { t } = useTranslation();
  const { getRestrictions } = useRestrictions();
  const { canEditRecurringRules, canEditOffers } = getRestrictions([takerMarketWithAutomation.takerMarket]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const chipLabel = useMemo(() => {
    if (!automation.offerSchedule) {
      return t('core.na');
    }

    return buildCustomScheduleSentenceFromExistingOfferSchedule({
      offerSchedule: automation.offerSchedule,
      t,
    });
  }, [automation.offerSchedule, t]);

  const handleDeleteModalOpen = () => {
    setOpenDeleteModal(true);
  };

  return (
    <>
      <DeleteScheduleModal
        onClose={() => setOpenDeleteModal(false)}
        open={openDeleteModal}
        takerMarket={takerMarketWithAutomation.takerMarket}
      />
      <div className="flex items-center gap-4 rounded bg-canvas p-4">
        {asChild && (
          <div className="shrink-0 grow-0">
            <CalendarLinesIcon className="h-9 w-9" fill={colors.gray[700]} title="Offer Schedules Indicator" />
          </div>
        )}
        <div
          className={cn('flex w-full items-center justify-between gap-10', {
            'flex-wrap gap-2 md:flex-nowrap md:gap-10': !asChild,
          })}
        >
          <div className="space-y-2">
            <div className={cn('text-sm font-medium text-text-secondary')}>{t('scheduling.participateOnSchedule')}</div>
            {!!automation.offerSchedule && <Chip size="sm" type="secondary" variant="outlined" label={chipLabel} />}
          </div>
          <div className="flex items-center gap-1">
            <Tooltip>
              <Tooltip.Trigger asChild>
                <IconButtonLink
                  icon={ArrowUpRightFromSquareIcon}
                  name={t('automation.editSchedule')}
                  to={asEnterpriseLink(
                    `customers/${takerMarketWithAutomation.takerMarket.makerOrganizationUuid}/markets/${takerMarketWithAutomation.takerMarket.marketUuid}/division/${takerMarketWithAutomation.takerMarket.takerDivisionId}/invoices/eligible`
                  )}
                  size="sm"
                  variant="ancillary"
                />
              </Tooltip.Trigger>
              <Tooltip.Content arrow side="left" variant="dark" className="max-w-60 whitespace-pre-wrap text-center">
                {t('automation.editSchedule')}
              </Tooltip.Content>
            </Tooltip>

            <Divider vertical />
            <IconButton
              className="bg-transparent"
              disabled={!canEditRecurringRules || !canEditOffers}
              icon={TrashCanIcon}
              inverse
              name={t('automation.deleteSchedule')}
              onClick={handleDeleteModalOpen}
              size="sm"
              variant="destructive"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default MiniScheduleManager;
